import { useState, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Loader2, ShieldAlert, AlertTriangle, Building2, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { useCarePlan } from '@/hooks/useCarePlan';
import { PlanHeader } from '@/components/care-plan/PlanHeader';
import { SavingsBanner } from '@/components/care-plan/SavingsBanner';
import { CostComparisonChart } from '@/components/care-plan/CostComparisonChart';
import { CarePlanMap, HOSPITAL_COLORS } from '@/components/care-plan/CarePlanMap';
import { CareCalendar } from '@/components/care-plan/CareCalendar';
import { ShareSection } from '@/components/care-plan/ShareSection';
import BackgroundMedia from '@/components/BackgroundMedia';

function formatUsd(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
}

export default function CarePlan() {
  const { planId } = useParams<{ planId: string }>();
  const [searchParams] = useSearchParams();
  const shareToken = searchParams.get('t');
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;

  const { plan, scenarios, assignments, isLoading, error, isAuthorized } = useCarePlan(planId, shareToken);
  const [selectedHospitalId, setSelectedHospitalId] = useState<string | null>(null);

  // Dedupe hospitals from assignments for the sidebar
  const hospitals = useMemo(() => {
    const map = new Map<string, {
      id: string; name: string; city: string | null; state: string | null;
      distance: number | null; totalCost: number; procedureCount: number; colorIndex: number;
    }>();
    let idx = 0;
    for (const a of assignments) {
      if (!a.assigned_hospital_id || a.is_blended) continue;
      const existing = map.get(a.assigned_hospital_id);
      if (existing) {
        existing.totalCost += a.hospital_total_price || 0;
        existing.procedureCount += 1;
      } else {
        map.set(a.assigned_hospital_id, {
          id: a.assigned_hospital_id,
          name: a.assigned_hospital_name || 'Unknown',
          city: a.assigned_hospital_city,
          state: a.assigned_hospital_state,
          distance: a.distance_miles,
          totalCost: a.hospital_total_price || 0,
          procedureCount: 1,
          colorIndex: idx++,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.totalCost - a.totalCost);
  }, [assignments]);

  const blendedCount = assignments.filter(a => a.is_blended).length;

  // Page title
  const pageTitle = plan?.plan_name || 'Care Plan';

  // --- Error / loading states ---
  if (!planId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className={`text-2xl font-bold ${isAhp ? (ahpDark ? 'text-civic-warm' : 'text-gray-900') : 'text-foreground'}`}>
            No Plan ID
          </h1>
          <p className={isAhp ? (ahpDark ? 'text-gray-400' : 'text-gray-600') : 'text-muted-foreground'}>
            Please provide a care plan ID in the URL.
          </p>
          <Link to="/family">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Family Planner
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-3">
          <Loader2 className={`w-8 h-8 animate-spin mx-auto ${isAhp ? 'text-civic-navy' : 'text-matrix-green'}`} />
          <p className={isAhp ? (ahpDark ? 'text-gray-400' : 'text-gray-600') : 'text-muted-foreground'}>
            Loading care plan...
          </p>
        </div>
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <AlertTriangle className="w-10 h-10 text-red-500 mx-auto" />
          <h1 className={`text-2xl font-bold ${isAhp ? (ahpDark ? 'text-civic-warm' : 'text-gray-900') : 'text-foreground'}`}>
            Plan Not Found
          </h1>
          <p className={isAhp ? (ahpDark ? 'text-gray-400' : 'text-gray-600') : 'text-muted-foreground'}>
            {error || 'This care plan does not exist or has been removed.'}
          </p>
          <Link to="/family">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Family Planner
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <ShieldAlert className="w-10 h-10 text-yellow-500 mx-auto" />
          <h1 className={`text-2xl font-bold ${isAhp ? (ahpDark ? 'text-civic-warm' : 'text-gray-900') : 'text-foreground'}`}>
            Access Denied
          </h1>
          <p className={isAhp ? (ahpDark ? 'text-gray-400' : 'text-gray-600') : 'text-muted-foreground'}>
            This care plan is private. You need a valid share link to view it.
          </p>
          <Link to="/family">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Family Planner
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (plan.status === 'pending' || plan.status === 'computing') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-3 max-w-sm px-4">
          <Loader2 className={`w-8 h-8 animate-spin mx-auto ${isAhp ? 'text-civic-navy' : 'text-matrix-green'}`} />
          <h1 className={`text-xl font-bold ${isAhp ? (ahpDark ? 'text-civic-warm' : 'text-gray-900') : 'text-foreground'}`}>
            {plan.status === 'pending' ? 'Preparing Your Care Plan' : 'Optimizing Your Care Plan'}
          </h1>
          <p className={isAhp ? (ahpDark ? 'text-gray-400' : 'text-gray-600') : 'text-muted-foreground'}>
            {plan.status === 'pending'
              ? 'Your plan is queued for optimization. This page will update automatically...'
              : `Finding the cheapest hospitals for each procedure near ${plan.location_label || 'your location'}...`
            }
          </p>
          <p className={`text-xs ${isAhp ? (ahpDark ? 'text-gray-500' : 'text-gray-400') : 'text-muted-foreground/60'}`}>
            This typically takes 10-30 seconds.
          </p>
        </div>
      </div>
    );
  }

  if (plan.status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <AlertTriangle className="w-10 h-10 text-red-500 mx-auto" />
          <h1 className={`text-xl font-bold ${isAhp ? (ahpDark ? 'text-civic-warm' : 'text-gray-900') : 'text-foreground'}`}>
            Optimization Failed
          </h1>
          <p className={isAhp ? (ahpDark ? 'text-gray-400' : 'text-gray-600') : 'text-muted-foreground'}>
            {plan.error_message || 'Something went wrong during optimization.'}
          </p>
        </div>
      </div>
    );
  }

  // --- Main ready state ---
  return (
    <>
      <Helmet>
        <title>{pageTitle} | {brand.name}</title>
      </Helmet>

      {!isAhp && <BackgroundMedia />}

      <div className={`min-h-screen ${isAhp ? (ahpDark ? 'bg-civic-bg-dark' : 'bg-[#F7F4EF]') : ''}`}>
        <div className="max-w-7xl mx-auto px-4 py-6 pb-24 space-y-5">
          {/* Back nav */}
          <Link
            to="/family"
            className={`inline-flex items-center gap-1.5 text-sm ${
              isAhp ? (ahpDark ? 'text-civic-navy-soft hover:text-[#9dbde0]' : 'text-civic-navy hover:text-[var(--civic-navy-deep)]') : 'text-matrix-green hover:text-matrix-green/80'
            }`}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Family Planner
          </Link>

          {/* Plan header */}
          <PlanHeader plan={plan} />

          {/* Savings banner */}
          {plan.status === 'ready' && plan.optimized_total_cost !== null && (
            <SavingsBanner plan={plan} />
          )}

          {/* Map + Hospital sidebar grid */}
          {assignments.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Map — takes 2/3 */}
              <div className="lg:col-span-2">
                <CarePlanMap
                  plan={plan}
                  assignments={assignments}
                  selectedHospitalId={selectedHospitalId}
                  onHospitalSelect={setSelectedHospitalId}
                />
              </div>

              {/* Hospital sidebar — takes 1/3 */}
              <div className={`rounded-xl border overflow-hidden flex flex-col ${
                isAhp ? (ahpDark ? 'border-civic-gold/20 bg-civic-card-dark' : 'border-gray-200 bg-white') : 'border-foreground/10 bg-card'
              }`}>
                <div className={`px-4 py-3 border-b ${
                  isAhp ? (ahpDark ? 'border-civic-gold/15' : 'border-gray-100') : 'border-foreground/10'
                }`}>
                  <h3 className={`text-sm font-semibold ${isAhp ? (ahpDark ? 'text-civic-warm' : 'text-gray-900') : 'text-foreground'}`}>
                    {hospitals.length} Hospital{hospitals.length !== 1 ? 's' : ''} Selected
                  </h3>
                  <p className={`text-xs mt-0.5 ${isAhp ? (ahpDark ? 'text-gray-500' : 'text-gray-400') : 'text-muted-foreground'}`}>
                    Click to highlight on map
                  </p>
                </div>

                <div className="flex-1 overflow-y-auto max-h-[320px] md:max-h-[340px]">
                  {hospitals.map(hosp => {
                    const color = HOSPITAL_COLORS[hosp.colorIndex % HOSPITAL_COLORS.length];
                    const isSelected = selectedHospitalId === hosp.id;

                    return (
                      <button
                        key={hosp.id}
                        onClick={() => setSelectedHospitalId(isSelected ? null : hosp.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b transition-colors ${
                          isSelected
                            ? isAhp
                              ? ahpDark ? 'bg-white/10 border-civic-gold/15' : 'bg-blue-50 border-gray-100'
                              : 'bg-matrix-green/10 border-foreground/5'
                            : isAhp
                              ? ahpDark ? 'hover:bg-white/5 border-civic-gold/8' : 'hover:bg-gray-50 border-gray-50'
                              : 'hover:bg-foreground/5 border-foreground/5'
                        }`}
                      >
                        {/* Color dot */}
                        <div
                          className="w-3 h-3 rounded-full shrink-0 border-2 border-white shadow-sm"
                          style={{ backgroundColor: color }}
                        />

                        {/* Hospital info */}
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs font-semibold truncate ${
                            isAhp ? (ahpDark ? 'text-civic-warm' : 'text-gray-900') : 'text-foreground'
                          }`}>
                            {hosp.name}
                          </p>
                          <p className={`text-[10px] flex items-center gap-1 ${
                            isAhp ? (ahpDark ? 'text-gray-500' : 'text-gray-400') : 'text-muted-foreground'
                          }`}>
                            <MapPin className="w-2.5 h-2.5" />
                            {[hosp.city, hosp.state].filter(Boolean).join(', ')}
                            {hosp.distance !== null && ` · ${hosp.distance.toFixed(0)}mi`}
                          </p>
                        </div>

                        {/* Cost + count */}
                        <div className="text-right shrink-0">
                          <p className={`text-xs font-bold ${isAhp ? (ahpDark ? 'text-civic-warm' : 'text-gray-900') : 'text-foreground'}`}>
                            {formatUsd(hosp.totalCost)}
                          </p>
                          <p className={`text-[10px] ${isAhp ? (ahpDark ? 'text-gray-500' : 'text-gray-400') : 'text-muted-foreground'}`}>
                            {hosp.procedureCount} proc
                          </p>
                        </div>
                      </button>
                    );
                  })}

                  {/* Blended / national avg fallback notice */}
                  {blendedCount > 0 && (
                    <div className={`px-4 py-2.5 text-[11px] ${
                      isAhp ? (ahpDark ? 'text-yellow-400/70 bg-yellow-900/10' : 'text-yellow-700 bg-yellow-50') : 'text-yellow-400/70 bg-yellow-500/5'
                    }`}>
                      + {blendedCount} procedure{blendedCount !== 1 ? 's' : ''} using national avg (no nearby data)
                    </div>
                  )}
                </div>

                {/* View all link at bottom */}
                {hospitals.length > 0 && (
                  <div className={`px-4 py-2.5 border-t text-center ${
                    isAhp ? (ahpDark ? 'border-civic-gold/15' : 'border-gray-100') : 'border-foreground/10'
                  }`}>
                    <Link
                      to={`/hospital/${hospitals[0].id}`}
                      className={`text-[11px] underline ${
                        isAhp ? (ahpDark ? 'text-civic-navy-soft' : 'text-civic-navy') : 'text-matrix-green'
                      }`}
                    >
                      View hospital profiles
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Care Calendar — day-by-day timeline */}
          {scenarios.length > 0 && assignments.length > 0 && (
            <CareCalendar
              scenarios={scenarios}
              assignments={assignments}
              onHospitalSelect={setSelectedHospitalId}
              selectedHospitalId={selectedHospitalId}
            />
          )}

          {/* Cost comparison chart (useful for multi-scenario plans) */}
          {scenarios.length > 1 && (
            <CostComparisonChart scenarios={scenarios} />
          )}

          {/* Share section */}
          <ShareSection plan={plan} />
        </div>
      </div>
    </>
  );
}
